export type ReviewQuality = 0 | 1 | 2 | 3 | 4 | 5;

export interface SrsItem {
  id: string;
  easeFactor: number;
  intervalDays: number;
  repetitions: number;
  lapses: number;
  dueAt: string;
  lastReviewedAt: string | null;
}

export interface SrsOptions {
  minEaseFactor?: number;
  maxEaseFactor?: number;
  maxIntervalDays?: number;
  learningStepsMinutes?: number[];
  graduatingIntervalDays?: number;
  easyIntervalBonus?: number;
  hardIntervalMultiplier?: number;
  newIntervalDays?: number;
}

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;
const MINUTES_PER_DAY = 1440;

const DEFAULT_MIN_EASE_FACTOR = 1.3;
const DEFAULT_MAX_EASE_FACTOR = 2.5;
const DEFAULT_MAX_INTERVAL_DAYS = 365;
const DEFAULT_LEARNING_STEPS_MINUTES = [1, 10];
const DEFAULT_GRADUATING_INTERVAL_DAYS = 1;
const DEFAULT_EASY_INTERVAL_BONUS = 1.3;
const DEFAULT_HARD_INTERVAL_MULTIPLIER = 1.2;
const DEFAULT_NEW_INTERVAL_DAYS = 0;

const PREVIEW_REFERENCE_ISO = "2024-01-01T00:00:00.000Z";

interface ResolvedOptions {
  minEaseFactor: number;
  maxEaseFactor: number;
  maxIntervalDays: number;
  learningStepMinutes: number;
  secondLearningStepMinutes: number;
  graduatingIntervalDays: number;
  easyIntervalBonus: number;
  hardIntervalMultiplier: number;
  newIntervalDays: number;
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(Math.max(value, min), max);
}

function resolveOptions(options?: SrsOptions): ResolvedOptions {
  const minEaseFactor = options?.minEaseFactor ?? DEFAULT_MIN_EASE_FACTOR;
  const maxEaseFactor = Math.max(minEaseFactor, options?.maxEaseFactor ?? DEFAULT_MAX_EASE_FACTOR);
  const steps = options?.learningStepsMinutes?.filter((step) => Number.isFinite(step) && step >= 0);
  const learningSteps = steps && steps.length > 0 ? steps : DEFAULT_LEARNING_STEPS_MINUTES;
  const learningStepMinutes = learningSteps[0] ?? DEFAULT_LEARNING_STEPS_MINUTES[0] ?? 1;
  const secondLearningStepMinutes =
    learningSteps[1] ?? learningSteps[0] ?? DEFAULT_LEARNING_STEPS_MINUTES[1] ?? 10;
  return {
    minEaseFactor,
    maxEaseFactor,
    maxIntervalDays: options?.maxIntervalDays ?? DEFAULT_MAX_INTERVAL_DAYS,
    learningStepMinutes,
    secondLearningStepMinutes,
    graduatingIntervalDays: options?.graduatingIntervalDays ?? DEFAULT_GRADUATING_INTERVAL_DAYS,
    easyIntervalBonus: options?.easyIntervalBonus ?? DEFAULT_EASY_INTERVAL_BONUS,
    hardIntervalMultiplier: options?.hardIntervalMultiplier ?? DEFAULT_HARD_INTERVAL_MULTIPLIER,
    newIntervalDays: options?.newIntervalDays ?? DEFAULT_NEW_INTERVAL_DAYS,
  };
}

function toIso(ms: number): string {
  return new Date(ms).toISOString();
}

function daysFromNow(nowMs: number, days: number): string {
  return toIso(nowMs + Math.round(days * MS_PER_DAY));
}

function minutesFromNow(nowMs: number, minutes: number): string {
  return toIso(nowMs + Math.round(minutes * MS_PER_MINUTE));
}

function isValidQuality(quality: number): quality is ReviewQuality {
  return Number.isInteger(quality) && quality >= 0 && quality <= 5;
}

export function createSrsItem(id: string, options?: SrsOptions): SrsItem {
  const resolved = resolveOptions(options);
  const nowMs = Date.now();
  return {
    id,
    easeFactor: 2.5,
    intervalDays: 0,
    repetitions: 0,
    lapses: 0,
    dueAt: daysFromNow(nowMs, resolved.newIntervalDays),
    lastReviewedAt: null,
  };
}

export function isDue(item: SrsItem, now?: Date): boolean {
  const dueMs = Date.parse(item.dueAt);
  if (Number.isNaN(dueMs)) return false;
  const currentMs = (now ?? new Date()).getTime();
  return currentMs >= dueMs;
}

export function scheduleReview(
  item: SrsItem,
  quality: ReviewQuality,
  options?: SrsOptions,
  now?: Date,
): SrsItem {
  if (!isValidQuality(quality)) {
    throw new RangeError(`Invalid review quality: ${String(quality)}`);
  }
  const resolved = resolveOptions(options);
  const nowMs = (now ?? new Date()).getTime();
  const nowIso = toIso(nowMs);

  if (quality <= 2) {
    const easeFactor = clamp(
      item.easeFactor - 0.2,
      resolved.minEaseFactor,
      resolved.maxEaseFactor,
    );
    return {
      id: item.id,
      easeFactor,
      intervalDays: 0,
      repetitions: 0,
      lapses: item.lapses + 1,
      dueAt: minutesFromNow(nowMs, resolved.learningStepMinutes),
      lastReviewedAt: nowIso,
    };
  }

  const easeFactor = clamp(
    quality === 3 ? item.easeFactor - 0.14 : quality === 5 ? item.easeFactor + 0.1 : item.easeFactor,
    resolved.minEaseFactor,
    resolved.maxEaseFactor,
  );

  let intervalDays: number;
  if (item.repetitions === 0) {
    intervalDays = resolved.secondLearningStepMinutes / MINUTES_PER_DAY;
  } else if (item.repetitions === 1) {
    intervalDays = resolved.graduatingIntervalDays;
  } else if (item.repetitions === 2) {
    intervalDays = item.intervalDays * resolved.easyIntervalBonus;
  } else {
    const multiplier =
      quality === 3 ? resolved.hardIntervalMultiplier : item.easeFactor;
    intervalDays = item.intervalDays * multiplier;
  }
  intervalDays = Math.min(intervalDays, resolved.maxIntervalDays);

  return {
    id: item.id,
    easeFactor,
    intervalDays,
    repetitions: item.repetitions + 1,
    lapses: item.lapses,
    dueAt: daysFromNow(nowMs, intervalDays),
    lastReviewedAt: nowIso,
  };
}

export function qualityFromAccuracy(accuracyPercent: number): ReviewQuality {
  if (accuracyPercent >= 95) return 5;
  if (accuracyPercent >= 85) return 4;
  if (accuracyPercent >= 70) return 3;
  if (accuracyPercent >= 50) return 2;
  if (accuracyPercent >= 30) return 1;
  return 0;
}

export function previewIntervals(
  id: string,
  options?: SrsOptions,
): Record<ReviewQuality, number> {
  const reference = new Date(PREVIEW_REFERENCE_ISO);
  const referenceMs = reference.getTime();
  const qualities: ReviewQuality[] = [0, 1, 2, 3, 4, 5];
  const intervals = {} as Record<ReviewQuality, number>;
  for (const quality of qualities) {
    const fresh = createSrsItem(id, options);
    const scheduled = scheduleReview(fresh, quality, options, reference);
    intervals[quality] = (Date.parse(scheduled.dueAt) - referenceMs) / MS_PER_DAY;
  }
  return intervals;
}
